// import Styles from './css'
import Button from "@blocks/Button"
import EmphasizedContent from "@blocks/EmphasizedContent"
import Flex from "@blocks/Flex"
import Grid from "@blocks/Grid"
import useScopedState from "@hooks/useScopedState"
import { desktop, mTablet, tablet } from "@styles/mixins"
import { colors, grid } from "@styles/vars"
import React, { useCallback } from "react"
import { useRef } from "react"
import { useEffect } from "react"
import { Hooks, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table"
import styled, { css } from "styled-components"
import Add from "./components/Add"
import CurrentPage from "./components/CurrentPage"
import Filters from "./components/Filters"
import Header from "./components/Header"
import Page from "./components/Page"
import Pagination from "./components/Pagination"
import RowsPerPage from "./components/RowsPerPage"
import Search from "./components/Search"
import TableContext from "./context"
import useActions from "./hooks/useActions"
import useHiddenColumns from "./hooks/useHiddenColumns"
import { Props } from "./types"

const Table = <T extends object = object>({ data, columns, hiddenColumns, options }: Props<T>) => {
    const { current: dataByDefault } = useRef([])

    columns = useActions(columns, options?.actions)

    const table = useTable<T>(
        {
            columns,
            data: data || dataByDefault,
            initialState: { pageSize: options?.pageSize || 10 },
            options
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    )

    const { getTableProps, getTableBodyProps, headerGroups } = table

    useHiddenColumns<T>(table, hiddenColumns)

    return (
        <TableContext.Provider value={table}>
            <Grid gap="20px">
                <Head>
                    <Actions>
                        {options?.heading && <Heading><h3>{options?.heading}</h3></Heading>}
                        <Search />
                        {options?.add && <Add handle={options?.add} />}
                    </Actions>
                    <Pagination />
                </Head>
                {options?.filters && <Filters components={options.filters} />}
                <Container>
                    <table {...getTableProps()}>
                        <thead>
                            <tr {...headerGroups[0].getHeaderGroupProps()}>
                                {headerGroups[0].headers.map(column => (
                                    <Header column={column} {...column.getHeaderProps(column.getSortByToggleProps())} />
                                ))}
                            </tr>
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            <Page />
                        </tbody>
                    </table>
                </Container>
                <Footer>
                    <RowsPerPage />
                    <CurrentPage />
                </Footer>
            </Grid>
        </TableContext.Provider>
    )
}

export default React.memo(Table) as typeof Table

/* STYLES */
const Heading = styled.div`
    display: flex;
    align-items: center;
    
    h3 {
        line-height: 1;
    }
`

const Actions = styled.div`
    display: flex;
    flex-direction: column;

    & > div:not(:last-child) {
        margin-bottom: 20px;
    }

    ${tablet(css`
        flex-direction: row;

        & > div:not(:last-child) {
            margin-right: 30px;
            margin-bottom: 0;
        }
    `)}
`

const Head = styled.div`
    display: flex;
    flex-direction: column;

    & > div:not(:last-child) {
        margin-bottom: 20px;
    }

    ${desktop(css`
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        & > div:not(:last-child) {
            margin-bottom: 0;
        }
    `)}
`

const Container = styled.div`
    width: 100%;
    overflow: auto;

    table {
        min-width: 100%;
        border-collapse: collapse;
    }

    tbody {
        tr:nth-child(2n - 1) {
            td {
                background: ${colors.greyBackground};
            }
        }
    }

    td, th {
        padding: 15px 20px;

        &:first-child {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }

        &:last-child {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }
    }

    th {
        padding: 0 20px 15px;
    }
`

const Footer = styled.div`
    display: flex;
    flex-direction: column;

    & > div:not(:last-child) {
        margin-bottom: 20px;
    }

    ${tablet(css`
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        & > div:not(:last-child) {
            margin-bottom: 0;
        }
    `)}
`